/**
 * Il tool client del tracking delle interazioni.
 *
 * Non sa niente degli eventi che esistono: legge gli attributi data-track-* dei template, filtra su
 * quello che la configurazione dichiara acceso e manda i beacon. La whitelist vera sta sul server.
 *
 * Il vocabolario, valido per qualunque template del sito:
 *   data-track-expose="<evento>"   su un contenitore: un evento nel beacon di init, con data-track-count
 *                                  (e data-track-target, se il contenitore dice da dove vengono i dati)
 *   data-track-view="<evento>"     su un contenitore: un evento la prima volta che e' visibile per meta'
 *   data-track-click="<evento>"    su qualcosa di cliccabile: un evento al click, con target e position
 *   data-track-target / -position / -count   i campi della riga
 */

const ENDPOINT = '/api21/nav/interaction';
const CONFIG_ELEMENT_ID = 'nav-interaction-config';
const MAX_EVENTS_PER_BEACON = 10;
const VIEW_RATIO = 0.5;

let config = null;
let clicksBound = false;
// Un caricamento di pagina manda una sola volta ogni expose e ogni view.
const alreadySent = new Set();

function readConfig() {
    if (config !== null)
        return config;

    config = {enabled: false, events: []};
    const node = document.getElementById(CONFIG_ELEMENT_ID);
    if (!node)
        return config;

    try {
        const parsed = JSON.parse(node.textContent);
        config = {
            enabled: parsed.enabled === true,
            events: Array.isArray(parsed.events) ? parsed.events : []
        };
    } catch (e) {
        // Configurazione illeggibile: il tracking resta spento, la pagina non se ne accorge.
    }

    return config;
}

function isEnabled(eventName) {
    const current = readConfig();
    return current.enabled && !!eventName && current.events.indexOf(eventName) !== -1;
}

function pageInfo() {
    const data = document.body ? document.body.dataset : {};
    return {class: data.pageClass || '', id: data.pageId || ''};
}

function number(value) {
    if (value === undefined || value === null || value === '')
        return null;

    const parsed = parseInt(value, 10);
    return isNaN(parsed) ? null : parsed;
}

function makeEvent(eventName, detail) {
    const event = {event: eventName};
    const options = detail || {};

    if (options.target)
        event.target = options.target;

    const position = number(options.position);
    if (position !== null)
        event.position = position;

    const count = number(options.count);
    if (count !== null)
        event.count = count;

    return event;
}

function send(events) {
    if (!events.length)
        return;

    // Il corpo dev'essere JSON valido comunque vada: il servlet lo parsa fuori dal suo try/catch,
    // e un corpo malformato diventerebbe una notifica di errore. Solo il corpo vuoto passa senza
    // notifica, come richiesta senza parametri: lo mandano i crawler, mai questo modulo.
    const payload = JSON.stringify({page: pageInfo(), events: events.slice(0, MAX_EVENTS_PER_BEACON)});

    if (navigator.sendBeacon) {
        const blob = new Blob([payload], {type: 'application/json'});
        if (navigator.sendBeacon(ENDPOINT, blob))
            return;
    }

    fetch(ENDPOINT, {
        method: 'POST',
        body: payload,
        headers: {'Content-Type': 'application/json'},
        keepalive: true
    }).catch(() => {
        // Un evento perso non e' un problema di chi sta comprando.
    });
}

function sendOnce(key, event) {
    if (alreadySent.has(key))
        return;

    alreadySent.add(key);
    send([event]);
}

/** Manda un evento da codice, per chi non puo' usare gli attributi nel template. */
export function track(eventName, detail) {
    if (!isEnabled(eventName))
        return;

    send([makeEvent(eventName, detail)]);
}

function sendExposeBatch(root) {
    const events = [];
    root.querySelectorAll('[data-track-expose]').forEach(element => {
        const eventName = element.dataset.trackExpose;
        if (!isEnabled(eventName) || alreadySent.has(eventName))
            return;

        alreadySent.add(eventName);
        events.push(makeEvent(eventName, {target: element.dataset.trackTarget, count: element.dataset.trackCount}));
    });

    // Tutti gli expose di una pagina in una richiesta sola: ogni beacon paga il filtro di sessione.
    send(events);
}

function observeViews(root) {
    if (!window.IntersectionObserver)
        return;

    const observer = new IntersectionObserver(entries => {
        entries.forEach(entry => {
            if (!entry.isIntersecting)
                return;

            observer.unobserve(entry.target);
            const eventName = entry.target.dataset.trackView;
            if (isEnabled(eventName))
                sendOnce(eventName, makeEvent(eventName, {
                    target: entry.target.dataset.trackTarget,
                    count: entry.target.dataset.trackCount
                }));
        });
    }, {threshold: VIEW_RATIO});

    root.querySelectorAll('[data-track-view]').forEach(element => {
        if (isEnabled(element.dataset.trackView))
            observer.observe(element);
    });
}

function listenClicks() {
    if (clicksBound)
        return;

    clicksBound = true;
    // In bubbling e mai in capture: i tasti che fermano la propagazione (l'aggiunta rapida) non
    // devono mandare due eventi, e il click qui non viene mai fermato ne' annullato.
    document.addEventListener('click', event => {
        const element = event.target.closest ? event.target.closest('[data-track-click]') : null;
        if (!element)
            return;

        const eventName = element.dataset.trackClick;
        if (!isEnabled(eventName))
            return;

        send([makeEvent(eventName, {
            target: element.dataset.trackTarget,
            position: element.dataset.trackPosition
        })]);
    });
}

export function initInteractions(root = document) {
    if (!readConfig().enabled)
        return;

    sendExposeBatch(root);
    observeViews(root);
    listenClicks();
}
